import {
  Box,
  Button,
  Flex,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { BaseFieldEditor } from './BaseFieldEditor';
import { colors } from 'packages/common-frontend/colors';
import {
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon as FinleyIcon } from 'packages/common-frontend/components/icons';
import * as utils from 'packages/common-frontend/utils';

const getTypeSpecificFields = (baseField: any) => {
  switch (baseField.type) {
    case 'boolean':
    case 'optionalBoolean':
      return <Text>externalBooleanTrue: {baseField.externalBooleanTrue}</Text>;
    case 'currency':
    case 'optionalCurrency':
      return <Text>currencyCode: {baseField.currencyCode}</Text>;
    case 'date':
    case 'optionalDate':
      return <Text>externalDateFormat: {baseField.externalDateFormat}</Text>;
    case 'dateTime':
    case 'optionalDateTime':
      return (
        <>
          <Text>
            externalDateTimeFormat: {baseField.externalDateTimeFormat}
          </Text>
          {baseField.defaultTz && <Text>defaultTz: {baseField.defaultTz}</Text>}
        </>
      );
    default:
      return null;
  }
};

const getDefaultToValue = (
  baseField: CreditAgreement.DataTapeField.BaseConfig,
) => {
  const { type } = baseField;
  switch (type) {
    case 'string':
    case 'integer':
    case 'float':
    case 'boolean':
    case 'currency':
    case 'date':
    case 'dateTime':
    case 'percentage':
      return baseField.defaultTo;

    // defaultTo exists only on 'non-optional' fields
    case 'optionalString':
    case 'optionalInteger':
    case 'optionalFloat':
    case 'optionalBoolean':
    case 'optionalCurrency':
    case 'optionalDate':
    case 'optionalDateTime':
    case 'optionalPercentage':
      return null;

    default:
      return finleyAssertUnreachable(type);
  }
};

let placeHolderNumber = 0;
export const LoanProductBaseFieldConfig: React.FC<{
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  onUpdate: (baseFields: CreditAgreement.DataTapeField.BaseConfig[]) => void;
}> = ({ baseFields, onUpdate }) => {
  const [editIndex, setEditIndex] = React.useState(-1);

  const onSingleFieldUpdate = (
    updatedBaseField: CreditAgreement.DataTapeField.BaseConfig,
    index: number,
  ) => {
    // eslint-disable-next-line no-console
    console.log({ updatedBaseField, index });
    onUpdate(
      baseFields.map((baseField, i) =>
        i === index ? updatedBaseField : baseField,
      ),
    );
  };

  const onAddField = () => {
    // eslint-disable-next-line no-plusplus
    placeHolderNumber++;
    const placeHolderField: CreditAgreement.DataTapeField.BaseConfig = {
      field: `placeholder_${placeHolderNumber}`,
      type: 'optionalString',
      description: 'Update the description',
    };
    onUpdate([placeHolderField, ...baseFields]);
  };

  const onDeleteField = (index: number) => {
    onUpdate(baseFields.filter((_, i) => i !== index));
  };

  return (
    <Box mt="20px" mb="35px" width="100%">
      <Text fontWeight="bold" fontSize="20px">
        Raw Base Fields
      </Text>
      <Flex mt="16px" mb="24px" alignItems="flex-end">
        <Text
          mt="8px"
          color={colors.black[70]}
          fontSize="16px"
          fontWeight={400}
        >
          Finley will sanitize the base fields in the CSV provided to the
          Capital Provider. You can view the sanitized base fields in the
          Borrowing Base Configuration.
        </Text>
        <Spacer />
        <Button
          aria-label="Add Field"
          minW="fit-content"
          ml="24px"
          border="2px"
          borderRadius="8px"
          borderColor={colors.aurora[400]}
          backgroundColor="white"
          onClick={onAddField}
          disabled={false}
        >
          <FinleyIcon icon="plus" color={colors.aurora[400]} />
          <Text ml="16px" color={colors.aurora[400]}>
            Add Field
          </Text>
        </Button>
      </Flex>

      <Table mt="16px" borderWidth="1px">
        <Thead>
          <Tr>
            <Th>Field</Th>
            <Th>Type</Th>
            <Th>Description</Th>
            <Th>Default Value</Th>
            <Th>Other Properties</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {baseFields.map((baseField, index) => {
            return (
              <Tr key={baseField.field}>
                <Td>{baseField.field}</Td>
                <Td>{baseField.type}</Td>
                <Td>{baseField.description}</Td>
                <Td>
                  {utils.displayValueOrEmpty(getDefaultToValue(baseField))}
                </Td>
                <Td>{getTypeSpecificFields(baseField)}</Td>
                <Td>
                  <EditIconButton
                    ariaLabel="edit"
                    label="Edit"
                    onClick={() => {
                      setEditIndex(index);
                    }}
                  />
                  <DeleteIconButton
                    ariaLabel="delete"
                    label="Delete"
                    onClick={() => onDeleteField(index)}
                    isDisabled={false}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      {editIndex !== -1 && (
        <BaseFieldEditor
          onClose={() => setEditIndex(-1)}
          onUpdate={(updated) => onSingleFieldUpdate(updated, editIndex)}
          baseField={baseFields[editIndex]}
        />
      )}
    </Box>
  );
};
